import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGitlabProvisioningConnectMutation } from 'network/mutations/useGitlabProvisioningConnectMutation';
import { useGitlabProvisionMutation } from 'network/mutations/useGitlabProvisionMutation';
import { useGitlabProvisionStatusQuery } from 'network/queries/useGitlabProvisionStatusQuery';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { ExternalLink } from 'components/ExternalLink';
import { prependHttpsIfNeeded } from '../../GithubManifestInstallationDialog/utils';
import {
  CONNECT_FIELDS,
  FORM_IDS,
  GENERIC_PROVISIONING_ERROR_KEY,
} from '../consts';
import { useGitlabInstallationV2 } from '../gitlabInstallationV2Context';
import { getConnectFormSchema } from '../schemas';
import { GITLAB_V2_HOST, type ConnectFormData } from '../types';
import {
  detectHost,
  getConnectDescriptionKey,
  getConnectErrorKey,
  getConnectUrlPlaceholderKey,
  getConnectUrlPrefill,
  getLegacyPatCreationUrl,
  getPatCreationUrl,
  getProvisionErrorKey,
  getSingleProvisionResult,
  getTransportErrorKey,
} from '../utils';

/**
 * Connect step — one PAT-only shape for every segment. On Next it validates the
 * URL client-side, then (with the Next button showing a spinner) runs
 * `connect` → `provision` → poll provision status. On `done` it stashes the
 * installation id and advances to Select. Any failure — connect validation,
 * bot provisioning, or a lost session — keeps the user on this screen with a
 * reason-specific message, so provisioning has no separate step.
 */
export const Connect = () => {
  const { setIsPending, goNext } = useMultiStepDialog();
  const {
    gitlabUrl,
    setGitlabUrl,
    setAccessToken,
    setConnectResult,
    setIsMultiGroupCloud,
    setInstallationId,
  } = useGitlabInstallationV2();
  const { mutate: connect } = useGitlabProvisioningConnectMutation();
  const { mutate: startProvision } = useGitlabProvisionMutation();

  const [provisionId, setProvisionId] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const spawnConnect = spawn('dialogs.gitlab-installation-v2.steps.connect');
  const spawnConnectErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.connect.errors',
  );
  const spawnProvisionErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.provision.errors',
  );

  const methods = useForm<ConnectFormData>({
    resolver: zodResolver(getConnectFormSchema()),
    defaultValues: {
      [CONNECT_FIELDS.GITLAB_URL]: gitlabUrl || getConnectUrlPrefill(),
      [CONNECT_FIELDS.ACCESS_TOKEN]: '',
    },
  });

  const {
    data: provisionStatus,
    isError: isProvisionStatusError,
    error: provisionStatusError,
  } = useGitlabProvisionStatusQuery(provisionId);

  useEffect(() => {
    setIsPending(isSubmitting);
  }, [isSubmitting]);

  const fail = (message: string) => {
    setErrorMessage(message);
    setProvisionId(undefined);
    setIsSubmitting(false);
  };

  // Advance on `done`; surface provisioning failures on this screen. The single-install
  // path reads the one entry of the per-group provision `results`.
  useEffect(() => {
    if (!provisionStatus) return;

    const result = getSingleProvisionResult(provisionStatus);

    if (provisionStatus.status === 'done' && result?.installationId) {
      setInstallationId(result.installationId);
      goNext();
    } else if (provisionStatus.status === 'done') {
      // Terminal `done` with no installation id (contract violation): polling
      // has stopped, so fall back to a generic failure instead of hanging.
      fail(spawnProvisionErrors(GENERIC_PROVISIONING_ERROR_KEY));
    } else if (provisionStatus.status === 'failed') {
      fail(
        spawnProvisionErrors(
          getProvisionErrorKey(
            result?.error?.code ?? provisionStatus.error?.code,
            detectHost(gitlabUrl),
          ),
        ),
      );
    }
  }, [provisionStatus, gitlabUrl]);

  useEffect(() => {
    if (isProvisionStatusError) {
      fail(
        spawnProvisionErrors(getTransportErrorKey(provisionStatusError, 404)),
      );
    }
  }, [isProvisionStatusError]);

  const formClassName = cn(
    "[&_[data-slot='form-label']]:font-mono",
    "[&_[data-slot='form-label']]:text-xs",
    "[&_[data-slot='form-label']]:uppercase",
  );

  const onSubmit = (formData: ConnectFormData) => {
    const normalizedUrl = prependHttpsIfNeeded(formData.gitlabUrl.trim());

    setErrorMessage(undefined);
    setProvisionId(undefined);
    setIsSubmitting(true);

    connect(
      { gitlabUrl: normalizedUrl, accessToken: formData.accessToken },
      {
        onSuccess: (result) => {
          setGitlabUrl(normalizedUrl);
          // Kept for the wizard's later stateless calls (provision/select/install).
          setAccessToken(formData.accessToken);
          setConnectResult({
            hostType: result.hostType,
            baseUrl: result.baseUrl,
            edition: result.edition,
            licensePlan: result.licensePlan ?? undefined,
            botType: result.botType,
            topLevelGroup: result.topLevelGroup ?? undefined,
            needsGroupSelection: result.needsGroupSelection,
          });

          // Cloud connected host-only: the user picks top-level groups on Select and
          // provisioning fans out per group at Install — so skip provisioning here.
          const isMultiGroupCloud =
            result.hostType === GITLAB_V2_HOST.CLOUD &&
            result.needsGroupSelection;

          setIsMultiGroupCloud(isMultiGroupCloud);

          if (isMultiGroupCloud) {
            setIsSubmitting(false);
            goNext();

            return;
          }

          // Self-managed / legacy single-group cloud: provision the one install now.
          startProvision(
            {
              gitlabUrl: normalizedUrl,
              accessToken: formData.accessToken,
            },
            {
              onSuccess: (provision) => setProvisionId(provision.provisionId),
              // Provision-start re-validates the URL/PAT and raises the same typed
              // connect errors as /connect (bad token, host unreachable, claim,
              // requires_instance_admin), so map the code to its connect copy
              // rather than always showing generic.
              onError: (err) =>
                fail(
                  spawnConnectErrors(
                    getConnectErrorKey(err, detectHost(normalizedUrl)),
                  ),
                ),
            },
          );
        },
        onError: (err) =>
          fail(
            spawnConnectErrors(
              getConnectErrorKey(err, detectHost(normalizedUrl)),
            ),
          ),
      },
    );
  };

  const gitlabUrlValue = methods.watch(CONNECT_FIELDS.GITLAB_URL);
  const patUrl = getPatCreationUrl(gitlabUrlValue);
  const legacyPatUrl = getLegacyPatCreationUrl(gitlabUrlValue);

  return (
    <Step
      title={spawnConnect('title')}
      description={spawnConnect(getConnectDescriptionKey())}
    >
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.CONNECT } as Record<string, string>)}
      >
        <Form.Input
          name={CONNECT_FIELDS.GITLAB_URL}
          label={spawnConnect('gitlab-url.label')}
          placeholder={spawnConnect(getConnectUrlPlaceholderKey())}
        />
        <div className="flex flex-col gap-1 text-sm text-text-general-neutral-mild">
          <ExternalLink url={patUrl} className="w-fit">
            {spawnConnect('create-token-link')}
          </ExternalLink>
          {legacyPatUrl && (
            <ExternalLink url={legacyPatUrl} className="w-fit">
              {spawnConnect('create-token-link-legacy')}
            </ExternalLink>
          )}
          <span>{spawnConnect('required-scopes')}</span>
          <span>{spawnConnect('token-note')}</span>
        </div>
        <Form.Input
          type="password"
          name={CONNECT_FIELDS.ACCESS_TOKEN}
          label={spawnConnect('access-token.label')}
          placeholder={spawnConnect('access-token.placeholder')}
        />
      </Form>

      {errorMessage && (
        <div className="mt-4">
          <InstallationStatusMessage status="error" message={errorMessage} />
        </div>
      )}
    </Step>
  );
};
